import { Injectable } from '@angular/core';
import { TextKeys } from '../../i18n/text-keys';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { VisumService } from '../visum/visum.service';
import { DateRange } from '../../models/date-range';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { AbsenceSelectionService } from './absence-selection.service';
import { AbsenceItemsService } from '../../state-mangement/entities/absence-items/absence-items.service';
import { AbsenceBackendService } from './absence-backend.service';
import {
  AbsenceAutoTimeEntry,
  AbsenceAutoTimeEntryAdmin,
  ChronoUserPersonDateRange,
  PersonDateRange,
  TaskResult,
} from '../../models/client-models';
import { ToastService } from '../toast-service';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import moment from 'moment';
import { TIMEZONE } from '../language.service';
import { AlertService } from '../alert.service';
import { SaldoBackendService } from '../saldo/saldo-backend.service';
import { SaldoRequestModel } from '../../models/saldo/saldo-request.model';

@Injectable({
  providedIn: 'root',
})
export class AbsenceEditService {
  private absenceModal: HTMLIonModalElement;
  private subscriptions = new Subscription();

  constructor(
    private alertController: AlertController,
    private translateService: TranslateService,
    private absenceBackendService: AbsenceBackendService,
    private toastService: ToastService,
    private visumService: VisumService,
    private absenceSelectionService: AbsenceSelectionService,
    private absenceItemsService: AbsenceItemsService,
    private alertService: AlertService,
    private saldoBackendService: SaldoBackendService
  ) {}

  deleteAbsence(
    visumRemovable: boolean,
    dateRange: PersonDateRange,
    admiaUserID: number,
    loggedInPersonID: number,
    callback: Function
  ) {
    if (visumRemovable) {
      this.visumService
        .createVisumAlert(
          this.askForAbsenceDeletion.bind(this),
          dateRange.startDate,
          dateRange.personID,
          admiaUserID,
          loggedInPersonID,
          callback,
          TextKeys.timeEntryForm.visumRemoveTitle,
          TextKeys.timeEntryForm.visumQuestion
        )
        .subscribe((alert) => alert.present());
    } else {
      this.askForAbsenceDeletion(
        dateRange,
        admiaUserID,
        loggedInPersonID,
        callback
      );
    }
  }

  createAbsence(
    visumRemovable: boolean,
    dateRange: PersonDateRange,
    admiaUserID: number,
    loggedInPersonID: number,
    callback: Function
  ) {
    if (visumRemovable) {
      this.visumService
        .createVisumAlert(
          this.createNewAbsenceInRange.bind(this),
          dateRange.startDate,
          dateRange.personID,
          admiaUserID,
          loggedInPersonID,
          callback,
          TextKeys.timeEntryForm.visumRemoveTitle,
          TextKeys.timeEntryForm.visumQuestion
        )
        .subscribe((alert) => alert.present());
    } else {
      this.createNewAbsenceInRange(
        dateRange,
        admiaUserID,
        loggedInPersonID,
        callback
      );
    }
  }

  public deleteAbsenceUserOrAdmin(
    dateRange: PersonDateRange,
    admiaUserID: number,
    loggedInPersonID: number
  ): Observable<TaskResult> {
    const startDate = dateRange.startDate;
    const endDate = dateRange.endDate;

    if (dateRange.personID === loggedInPersonID) {
      const absenceRange = {
        personID: dateRange.personID,
        startDate,
        endDate,
      } as PersonDateRange;
      return this.absenceBackendService.deleteAbsence(absenceRange);
    } else {
      const absenceRange = {
        personID: dateRange.personID,
        startDate: startDate,
        endDate: endDate,
        chrnoUserID: admiaUserID,
      } as ChronoUserPersonDateRange;

      return this.absenceBackendService.deleteAbsenceAsChronoUser(absenceRange);
    }
  }

  public createAbsenceUserOrAdmin(
    dateRange: PersonDateRange,
    idAbsence: number,
    admiaUserID: number,
    loggedInPersonID: number
  ): Observable<TaskResult> {
    if (loggedInPersonID === dateRange.personID) {
      return this.absenceBackendService.createAbsences({
        idPerson: dateRange.personID,
        startDate: moment(dateRange.startDate).format(),
        endDate: moment(dateRange.endDate).format(),
        id: idAbsence,
      } as AbsenceAutoTimeEntry);
    } else {
      return this.absenceBackendService.createAbsencesAsAdmin({
        idPerson: dateRange.personID,
        idChronoUser: admiaUserID,
        startDate: moment(dateRange.startDate).format(),
        endDate: moment(dateRange.endDate).format(),
        id: idAbsence,
      } as AbsenceAutoTimeEntryAdmin);
    }
  }

  /**
   * Create new Absence in Range. Shows selection modal and handles click events.
   */
  private createNewAbsenceInRange(
    personDateRange: PersonDateRange,
    admiaUserID: number,
    loggedInPersonID: number,
    callback: Function
  ) {
    const result = this.absenceSelectionService.createNewAbsenceSelection(
      personDateRange.startDate,
      personDateRange.endDate
    );

    result.overlay.subscribe((element) => {
      this.absenceModal = element;
      this.absenceModal.animated = false;
      this.absenceModal
        .present()
        .catch((error) =>
          this.toastService.presentErrorToast({ message: error?.error.message })
        );
    });

    this.subscriptions.add(
      result.onCancel.subscribe(() => {
        this.absenceItemsService.clearCache();
        callback();
        // this.timeEntryRowMovementDisabled$.next(false);
        this.absenceModal.dismiss().catch((error) =>
          this.toastService.presentErrorToast({
            message: error?.error.message,
          })
        );
      })
    );
    this.subscriptions.add(
      result.onSave.subscribe(async (value) => {
        const idPerson = personDateRange.personID;
        const startDate = new Date(personDateRange.startDate);
        const endDate = new Date(personDateRange.endDate);
        const absenceId = value.id;

        // Close the selection modal first so only the alert is visible
        if (this.absenceModal) {
          await this.absenceModal.dismiss().catch(() => {});
          this.absenceModal = null;
        }

        const proceed = () => {
          this.createAbsenceUserOrAdmin(
            personDateRange,
            value.id,
            admiaUserID,
            loggedInPersonID
          ).subscribe({
            next: (result) => {
              this.toastService.presentInfoToast({ message: result.message });
              this.absenceItemsService.clearCache();
              callback();
            },
            error: (error) => {
              this.toastService.presentErrorToast({
                message: error?.error?.message,
              });
            },
          });
        };

        const isHoliday =
          value?.absenceNr === '9' ||
          (value?.name && value.name.toLowerCase().includes('ferien')) ||
          (value?.name && value.name.toLowerCase().includes('ferien'));

        if (!isHoliday) {
          // Not Ferien → skip overdraft check and create directly
          proceed();
          return;
        }

        const check$ = this.absenceBackendService.checkOverdraft({
          idPerson,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          absenceId,
          // requestedHours: value.requestedHours
        });

        console.log('Performing overdraft check for absence creation:', {
          idPerson,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          absenceId,
        });

        check$.subscribe({
          next: (check) => {
            const warn =
              check?.successful && check.resultObject?.willBeNegative;

            console.log('Overdraft check result:', check);

            if (!warn) {
              // Kein negativer Saldo → direkt buchen
              proceed();
              return;
            }

            const r = check.resultObject;

            // einfache Format Funktion
            const fmt = (n: number | null | undefined) =>
              n == null ? '-' : (Math.round(Number(n) * 100) / 100).toFixed(2);

            // Wir bauen ein paar Zeilen zusammen, je nachdem was das Backend zurückgegeben hat
            const lines: string[] = [
              'Diese Eingabe führt zu einem negativen Ferien-Saldo. Trotzdem eintragen?',
              '',
            ];

            if (
              r.requestedHolidayHours != null ||
              r.requestedHolidayDays != null
            ) {
              const requestedParts: string[] = [];

              if (r.requestedHolidayHours != null) {
                const unitHours = this.translateService.instant(
                  TextKeys.badge.saldoUnitHours
                );
                requestedParts.push(
                  `${fmt(r.requestedHolidayHours)} ${unitHours}`
                );
              }

              if (r.requestedHolidayDays != null) {
                const unitDays = this.translateService.instant(
                  TextKeys.badge.saldoUnitDay
                );
                requestedParts.push(
                  `${fmt(r.requestedHolidayDays)} ${unitDays}`
                );
              }

              if (requestedParts.length) {
                lines.push(`Angefragt: ${requestedParts.join(' / ')}`);
              }
            }

            if (
              r.projectedHolidayHours != null ||
              r.projectedHolidayDays != null
            ) {
              const projectedParts: string[] = [];

              if (r.projectedHolidayHours != null) {
                const unitHours = this.translateService.instant(
                  TextKeys.badge.saldoUnitHours
                );
                projectedParts.push(
                  `${fmt(r.projectedHolidayHours)} ${unitHours}`
                );
              }

              if (r.projectedHolidayDays != null) {
                const unitDays = this.translateService.instant(
                  TextKeys.badge.saldoUnitDay
                );
                projectedParts.push(
                  `${fmt(r.projectedHolidayDays)} ${unitDays}`
                );
              }

              if (projectedParts.length) {
                lines.push(
                  `Prognose nach Eintrag: ${projectedParts.join(' / ')}`
                );
              }
            }

            const msg = lines.join('\n');

            this.alertService.showOkCancelAlert(
              msg,
              'Hinweis Ferien-Saldo',
              () => {
                proceed();
              },
              () => {},
              'overdraft-alert'
            );
          },
          error: () => {
            // Wenn der Check schiefgeht → lieber trotzdem buchen statt blockieren
            proceed();
          },
        });
      })
    );
  }

  /**
   * Prompt pop up to ask user if he is sure he wants to delete the absence.
   */
  private askForAbsenceDeletion(
    dateRange: PersonDateRange,
    admiaUserID: number,
    loggedInPersonID: number,
    callback: Function
  ) {
    fromPromise(
      this.alertController.create({
        cssClass: 'my-custom-class',
        header: this.translateService.instant(
          TextKeys.dayInfo.deleteAbsenceTitle
        ),
        message: this.translateService.instant(
          TextKeys.dayInfo.deleteAbsenceQuestion
        ),
        buttons: [
          {
            text: this.translateService.instant(TextKeys.general.cancel),
            role: 'cancel',
            cssClass: 'secondary',
            handler: () => {
              callback();
            },
          },
          {
            text: this.translateService.instant(TextKeys.dayInfo.delete),
            handler: () => {
              this.deleteAbsenceUserOrAdmin(
                dateRange,
                admiaUserID,
                loggedInPersonID
              ).subscribe((result) => {
                this.toastService.presentInfoToast({ message: result.message });
                callback();
              });
            },
          },
        ],
      })
    ).subscribe((alert) => {
      alert
        .present()
        .catch((error) =>
          this.toastService.presentErrorToast({ message: error?.error.message })
        );
    });
  }
}
